import { useCallback, useEffect, useState } from "react";
import { TicketPercent, Shuffle, Copy, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ErrorBox } from "@/components/staff/ui";
import { codeSummary, StatusBadge, TermsFields, EditCode, DeleteCode } from "@/components/staff/DiscountCodeParts";

const MONTHLY = [["starter_monthly", "Starter monthly"], ["plus_monthly", "Plus monthly"], ["unlimited_monthly", "Unlimited monthly"]];
const LABEL = {
  ...Object.fromEntries(MONTHLY), starter_annual: "Starter annual", plus_annual: "Plus annual", unlimited_annual: "Unlimited annual",
  starter_day: "Starter Pass", plus_day: "Plus Pass", unlimited_day: "Unlimited Pass",
};
const EMPTY = { percent_off: 20, code: "", expires_at: "", duration_months: "", max_customers: "", unlimited: false, plans: [] };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function ExpiryField({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const picked = value ? new Date(`${value}T12:00:00`) : undefined;
  return (
    <label className="space-y-1.5"><span className="text-xs text-neutral-500">Expires (optional)</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="w-full h-9 px-3 rounded-md border border-white/10 bg-white/5 text-sm text-left flex items-center gap-2" data-testid="discount-expiry-input">
            <CalendarDays className="h-4 w-4 text-neutral-500" />
            <span className={value ? "" : "text-neutral-500"}>{picked ? picked.toLocaleDateString() : "Never"}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[#0E121C] border-white/10" align="start">
          <Calendar mode="single" selected={picked} disabled={(d) => d < new Date(new Date().toDateString())}
            onSelect={(d) => { setOpen(false); onChange(d ? ymd(d) : ""); }} />
          {value && <button onClick={() => onChange("")} className="w-full text-xs py-2 border-t border-white/10 text-neutral-400 hover:text-white" data-testid="discount-expiry-clear">No expiry</button>}
        </PopoverContent>
      </Popover>
      <span className="block text-[11px] text-neutral-500">Controls when the code can be redeemed, not how long the discount lasts.</span>
    </label>
  );
}

function CodeForm({ onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setF({ ...f, plans: f.plans.includes(k) ? f.plans.filter((x) => x !== k) : [...f.plans, k] });
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/admin/discounts", {
        percent_off: Number(f.percent_off), code: f.code, plans: f.plans, expires_at: f.expires_at || null,
        duration_months: f.duration_months, max_customers: f.unlimited ? null : f.max_customers, unlimited: f.unlimited,
      });
      toast.success(`Created ${data.code}`);
      setF(EMPTY);
      onCreated();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-[#1B2740] bg-[#0E1626] p-6 space-y-5" data-testid="discount-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="space-y-1.5"><span className="text-xs text-neutral-500">Percent off (1–100)</span>
          <Input type="number" min={1} max={100} value={f.percent_off} onChange={(e) => setF({ ...f, percent_off: e.target.value })} className="bg-white/5 border-white/10" data-testid="discount-percent-input" /></label>
        <label className="space-y-1.5"><span className="text-xs text-neutral-500">Code (blank = random)</span>
          <div className="flex gap-1.5">
            <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="EXAMS25" className="bg-white/5 border-white/10 font-mono" data-testid="discount-code-input" />
            <button onClick={() => setF({ ...f, code: "" })} title="Use a random code" aria-label="Use a random code" className="px-2.5 rounded-lg border border-white/10 text-neutral-400 hover:text-white" data-testid="discount-random-btn"><Shuffle className="h-4 w-4" /></button>
          </div></label>
        <ExpiryField value={f.expires_at} onChange={(v) => setF({ ...f, expires_at: v })} />
      </div>
      <TermsFields prefix="discount" f={f} setF={setF} />
      <div>
        <p className="text-xs text-neutral-500 mb-2">Applies to (none selected = all three monthly plans)</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {MONTHLY.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
              <Checkbox checked={f.plans.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`discount-plan-${k}`} /> {l}
            </label>
          ))}
        </div>
        <p className="text-[11px] text-neutral-500 mt-2">Codes with a duration only work on monthly plans. Stripe refuses them on yearly plans and passes.</p>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-xs text-neutral-500">The months start when the customer's subscription starts with the discount. After that, Stripe charges the normal price automatically.</p>
        <button onClick={submit} disabled={busy} className="self-end sm:self-auto shrink-0 px-5 py-2 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 disabled:opacity-50" data-testid="discount-create-btn">Create code</button>
      </div>
    </div>
  );
}

function CodeRow({ c, onToggle, onSaved, onDeleted }) {
  const plans = c.plans.length ? c.plans.map((p) => LABEL[p] || p).join(", ") : c.rules === 2 ? "All monthly plans" : "All plans";
  const locked = c.status === "full" || (c.status === "expired" && !c.active);
  return (
    <li className="px-4 sm:px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center" data-testid={`discount-row-${c.code}`}>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => { navigator.clipboard?.writeText(c.code); toast.success("Copied"); }} className="font-mono text-sm flex items-center gap-1.5 hover:text-sky-300" data-testid={`discount-copy-${c.code}`}>{c.code}<Copy className="h-3 w-3 text-neutral-600" /></button>
          <StatusBadge c={c} />
        </div>
        <p className="text-sm text-neutral-200 break-words" data-testid={`discount-uses-${c.code}`}>{codeSummary(c)}</p>
        <p className="text-xs text-neutral-500">{plans} · {c.expires_at ? `Redeemable until ${new Date(c.expires_at).toLocaleDateString()}` : "No expiry date"}</p>
        {c.message && <p className="text-xs text-amber-300" data-testid={`discount-full-msg-${c.code}`}>{c.message}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Switch checked={c.active} disabled={locked} onCheckedChange={(v) => onToggle(c, v)} title={c.status === "full" ? "Raise Maximum customers to reopen it" : undefined} data-testid={`discount-active-${c.code}`} />
        <EditCode c={c} onSaved={onSaved} />
        <DeleteCode code={c.code} onDeleted={onDeleted} />
      </div>
    </li>
  );
}

export function DiscountCodes() {
  const [codes, setCodes] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    setErr("");
    return http.get("/admin/discounts").then((r) => setCodes(r.data)).catch((e) => setErr(formatError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const replace = (row) => setCodes((x) => x.map((y) => (y.code === row.code ? row : y)));
  const toggle = async (c, active) => {
    try {
      await http.post(`/admin/discounts/${c.code}/active`, { active });
      replace({ ...c, active, status: c.status === "expired" ? "expired" : active ? "active" : "off" });
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-5" data-testid="discount-codes">
      <p className="text-neutral-400 text-sm flex items-start gap-2"><TicketPercent className="h-4 w-4 text-sky-300 shrink-0 mt-0.5" />Students enter these on the Stripe checkout page. Stripe counts each completed payment and enforces the limit; a code that fills up is switched off automatically. Every change is saved in the audit log.</p>
      {err && <ErrorBox error={`Couldn't load discount codes: ${err}`} onRetry={load} testid="discount-error" />}
      <CodeForm onCreated={load} />
      <ul className="rounded-2xl border border-[#1B2740] bg-[#0E1626] divide-y divide-white/5" data-testid="discount-table">
        {!codes && !err && <li className="px-4 py-8 text-center text-neutral-500 text-sm" data-testid="discount-loading">Loading codes…</li>}
        {codes && !codes.length && <li className="px-4 py-8 text-center text-neutral-500 text-sm" data-testid="discount-empty">No codes yet.</li>}
        {codes?.map((c) => (
          <CodeRow key={c.code} c={c} onToggle={toggle} onSaved={replace} onDeleted={() => setCodes((x) => x.filter((y) => y.code !== c.code))} />
        ))}
      </ul>
    </div>
  );
}
